// Editor de tabela do guia de tamanhos (docs/specs/2026-09-23-guia-de-
// tamanhos-design.md secao 6): adicionar/remover/renomear colunas,
// adicionar/remover/reordenar linhas, celulas de texto, validacao RN-G03
// no cliente com mensagem clara. Mobile: rolagem horizontal DENTRO do
// editor (nunca da pagina) — o wrapper e overflow-x:auto com min-width na
// tabela, o card em volta nao estoura a viewport de 390px.
import { Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import type { SizeGuideRow } from "../lib/types";
import { SIZE_GUIDE_MAX_VALUE_LEN, emptySizeGuideRow, validateSizeGuideTable } from "../lib/size-guide";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

interface SizeGuideTableEditorProps {
  columns: string[];
  rows: SizeGuideRow[];
  onChange: (columns: string[], rows: SizeGuideRow[]) => void;
  disabled?: boolean;
}

export function SizeGuideTableEditor({
  columns,
  rows,
  onChange,
  disabled,
}: SizeGuideTableEditorProps): JSX.Element {
  const errors = validateSizeGuideTable(columns, rows);

  function renameColumn(idx: number, value: string): void {
    const next = columns.slice();
    next[idx] = value;
    onChange(next, rows);
  }

  function addColumn(): void {
    const next = [...columns, `Medida ${columns.length + 1}`];
    onChange(
      next,
      rows.map((r) => ({ ...r, values: [...r.values, ""] })),
    );
  }

  function removeColumn(idx: number): void {
    const next = columns.filter((_, i) => i !== idx);
    onChange(
      next,
      rows.map((r) => ({ ...r, values: r.values.filter((_, i) => i !== idx) })),
    );
  }

  function renameRowSize(idx: number, size: string): void {
    const next = rows.slice();
    const current = next[idx];
    if (!current) return;
    next[idx] = { ...current, size };
    onChange(columns, next);
  }

  function setCell(rowIdx: number, colIdx: number, value: string): void {
    const next = rows.slice();
    const current = next[rowIdx];
    if (!current) return;
    const values = current.values.slice();
    values[colIdx] = value;
    next[rowIdx] = { ...current, values };
    onChange(columns, next);
  }

  function addRow(): void {
    onChange(columns, [...rows, emptySizeGuideRow(columns.length)]);
  }

  function removeRow(idx: number): void {
    onChange(columns, rows.filter((_, i) => i !== idx));
  }

  function moveRow(idx: number, dir: -1 | 1): void {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    const [item] = next.splice(idx, 1);
    if (!item) return;
    next.splice(target, 0, item);
    onChange(columns, next);
  }

  const rowErrorAt = (idx: number): string | undefined =>
    errors.find((e) => e.field === "rows" && e.index === idx)?.message;
  const columnErrorAt = (idx: number): string | undefined =>
    errors.find((e) => e.field === "columns" && e.index === idx)?.message;
  const globalErrors = errors.filter((e) => e.index === undefined);

  return (
    <div className="stack" style={{ gap: ".5rem" }}>
      {/* wrapper com overflow-x:auto — a rolagem horizontal fica AQUI, nunca
          na pagina (handoff mobile 390px). min-width na tabela forca o
          scroll em vez de espremer as colunas ilegiveis. */}
      <div
        className="rounded-md border border-line"
        style={{ overflowX: "auto", maxWidth: "100%" }}
      >
        <table style={{ minWidth: "32rem", width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                style={{
                  padding: ".5rem",
                  textAlign: "left",
                  fontSize: "var(--text-sm)",
                  fontWeight: 800,
                  color: "var(--color-ink-2)",
                  borderBottom: ".0625rem solid var(--color-line)",
                  minWidth: "6rem",
                  position: "sticky",
                  left: 0,
                  background: "var(--color-card)",
                }}
              >
                Tamanho
              </th>
              {columns.map((c, i) => (
                <th
                  key={i}
                  style={{
                    padding: ".375rem",
                    borderBottom: ".0625rem solid var(--color-line)",
                    minWidth: "8rem",
                  }}
                >
                  <div className="cluster" style={{ gap: ".25rem" }}>
                    <Input
                      value={c}
                      onChange={(e) => renameColumn(i, e.target.value)}
                      disabled={disabled}
                      aria-invalid={Boolean(columnErrorAt(i))}
                      placeholder={`Coluna ${i + 1}`}
                      style={{ minHeight: "auto", height: "2rem", fontSize: "var(--text-sm)" }}
                    />
                    {!disabled ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeColumn(i)}
                        aria-label={`Remover coluna ${c || i + 1}`}
                        title="Remover coluna"
                      >
                        <Trash2 className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                </th>
              ))}
              <th style={{ padding: ".375rem", borderBottom: ".0625rem solid var(--color-line)", width: "1%" }}>
                {!disabled ? (
                  <Button type="button" variant="outline" size="sm" onClick={addColumn} title="Adicionar coluna">
                    <Plus className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
                  </Button>
                ) : null}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                <td
                  style={{
                    padding: ".375rem",
                    borderBottom: ".0625rem solid var(--color-line)",
                    position: "sticky",
                    left: 0,
                    background: "var(--color-card)",
                  }}
                >
                  <div className="cluster" style={{ gap: ".25rem" }}>
                    {!disabled ? (
                      <span
                        aria-hidden
                        style={{ color: "var(--color-ink-3)", display: "inline-flex", flexShrink: 0 }}
                      >
                        <GripVertical className="icon" style={{ width: ".875rem", height: ".875rem" }} />
                      </span>
                    ) : null}
                    <Input
                      value={r.size}
                      onChange={(e) => renameRowSize(ri, e.target.value)}
                      disabled={disabled}
                      aria-invalid={Boolean(rowErrorAt(ri))}
                      placeholder="Ex.: P, M, G, 38"
                      style={{ minHeight: "auto", height: "2rem", fontSize: "var(--text-sm)", width: "5.5rem" }}
                    />
                  </div>
                </td>
                {r.values.map((v, ci) => (
                  <td key={ci} style={{ padding: ".375rem", borderBottom: ".0625rem solid var(--color-line)" }}>
                    <Input
                      value={v}
                      onChange={(e) => setCell(ri, ci, e.target.value)}
                      disabled={disabled}
                      maxLength={SIZE_GUIDE_MAX_VALUE_LEN}
                      placeholder="—"
                      style={{ minHeight: "auto", height: "2rem", fontSize: "var(--text-sm)" }}
                    />
                  </td>
                ))}
                <td style={{ padding: ".375rem", borderBottom: ".0625rem solid var(--color-line)", whiteSpace: "nowrap" }}>
                  {!disabled ? (
                    <div className="cluster" style={{ gap: 0 }}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => moveRow(ri, -1)}
                        disabled={ri === 0}
                        aria-label="Mover linha para cima"
                      >
                        <ArrowUp className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => moveRow(ri, 1)}
                        disabled={ri === rows.length - 1}
                        aria-label="Mover linha para baixo"
                      >
                        <ArrowDown className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRow(ri)}
                        aria-label={`Remover linha ${r.size || ri + 1}`}
                      >
                        <Trash2 className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!disabled ? (
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden /> Adicionar tamanho
        </Button>
      ) : null}

      {errors.length > 0 ? (
        <div
          className="stack"
          style={{
            gap: ".25rem",
            background: "var(--color-bad-soft, var(--color-soft))",
            border: ".0625rem solid var(--color-bad)",
            borderRadius: "var(--radius-md)",
            padding: ".625rem .75rem",
          }}
        >
          {globalErrors.map((e, i) => (
            <p key={i} style={{ fontSize: "var(--text-sm)", color: "var(--color-bad)", fontWeight: 700 }}>
              {e.message}
            </p>
          ))}
          {rows.map((r, i) => {
            const msg = rowErrorAt(i);
            return msg ? (
              <p key={`row-${i}`} style={{ fontSize: "var(--text-sm)", color: "var(--color-bad)", fontWeight: 700 }}>
                {msg}
              </p>
            ) : null;
          })}
          {columns.map((c, i) => {
            const msg = columnErrorAt(i);
            return msg ? (
              <p key={`col-${i}`} style={{ fontSize: "var(--text-sm)", color: "var(--color-bad)", fontWeight: 700 }}>
                {msg}
              </p>
            ) : null;
          })}
        </div>
      ) : null}
    </div>
  );
}
